/**
 * Formulario de proveedor, compartido por /vendors/new y /vendors/:id/edit.
 * Mismo patrón que VehicleForm: el componente no sabe si crea o actualiza.
 */
import { useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'

export const VENDOR_TYPES = [
  'Mechanic',
  'Body Shop',
  'Tire Shop',
  'Parts Supplier',
  'Dealer',
  'Towing',
  'Fuel',
  'Insurance',
  'Other',
] as const

export interface VendorFormValues {
  companyName: string
  contactName: string | null
  phone: string | null
  email: string | null
  city: string | null
  vendorType: string
  notes: string | null
}

export interface VendorFormProps {
  initial?: Partial<VendorFormValues>
  submitLabel: string
  pending?: boolean
  error?: string | null
  fieldErrors?: Record<string, string>
  onSubmit: (values: VendorFormValues) => void
  extraActions?: React.ReactNode
}

export function VendorForm({
  initial,
  submitLabel,
  pending = false,
  error,
  fieldErrors = {},
  onSubmit,
  extraActions,
}: VendorFormProps) {
  const [companyName, setCompanyName] = useState(initial?.companyName ?? '')
  const [contactName, setContactName] = useState(initial?.contactName ?? '')
  const [phone, setPhone] = useState(initial?.phone ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [city, setCity] = useState(initial?.city ?? '')
  const [vendorType, setVendorType] = useState(initial?.vendorType ?? 'Other')
  const [notes, setNotes] = useState(initial?.notes ?? '')

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    onSubmit({
      companyName: companyName.trim(),
      contactName: contactName.trim() || null,
      phone: phone.trim() || null,
      // Vacío como null, no como '': la columna es citext con validación de
      // correo, y una cadena vacía no es una dirección.
      email: email.trim() || null,
      city: city.trim() || null,
      vendorType,
      notes: notes.trim() || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {error && (
        <p role="alert" className="text-sm font-medium text-danger">
          {error}
        </p>
      )}

      <Field
        label="Empresa"
        required
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        error={fieldErrors.companyName}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Contacto"
          value={contactName}
          onChange={(e) => setContactName(e.target.value)}
        />
        <Field
          label="Teléfono"
          type="tel"
          inputMode="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <Field
          label="Email"
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
        />
        <Field label="Ciudad" value={city} onChange={(e) => setCity(e.target.value)} />
      </div>

      <label className="flex flex-col gap-1.5 text-sm">
        <span className="font-medium">Tipo</span>
        <select
          className="h-11 rounded-lg border border-input bg-card px-3"
          value={vendorType}
          onChange={(e) => setVendorType(e.target.value)}
        >
          {VENDOR_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1.5 text-sm">
        <span className="font-medium">Notas</span>
        <textarea
          rows={3}
          className="rounded-lg border border-input bg-card px-3 py-2 text-sm"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      <div className="flex flex-col gap-2 pt-1 sm:flex-row-reverse">
        <Button type="submit" className="sm:flex-1" loading={pending}>
          {submitLabel}
        </Button>
        {extraActions}
      </div>
    </form>
  )
}
